import React, { useState, useEffect } from "react";
import { CheckCircle, Mail, Home, Ticket, ChevronDown, ChevronUp, Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { DidYouKnow } from "./DidYouKnow";
import { TicketQRCode } from "./TicketQRCode";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Culture Article Card Component (simplified for confirmation page)
const CultureArticleCard = ({ article }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Strip HTML for preview text
  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };
  
  const plainText = stripHtml(article.content || "");
  const previewLength = 150;
  const needsTruncation = plainText.length > previewLength;
  const previewText = needsTruncation 
    ? plainText.substring(0, previewLength) + "..."
    : plainText;

  const getCategoryColor = (category) => {
    switch(category) {
      case "Hip-Hop/RNB": return "from-pink-500 to-rose-500";
      case "Afrobeat": return "from-emerald-500 to-teal-500";
      case "Dancehall/Caraïbes": return "from-blue-500 to-cyan-500";
      default: return "from-primary to-primary/80";
    }
  };

  return (
    <div className="glass-card rounded-lg p-4 border border-border hover:border-primary/40 transition-all">
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getCategoryColor(article.category)} flex items-center justify-center flex-shrink-0`}>
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <span className={`inline-block px-2 py-0.5 bg-gradient-to-r ${getCategoryColor(article.category)} text-white text-xs font-semibold rounded mb-1`}>
            {article.category}
          </span>
          <h4 className="text-sm font-bold leading-tight">{article.title}</h4>
        </div>
      </div>
      
      <div className="text-sm text-muted-foreground leading-relaxed">
        {isExpanded ? (
          <div 
            className="prose prose-sm prose-invert max-w-none
              [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:text-foreground
              [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:text-foreground
              [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:text-foreground
              [&_p]:mb-2 [&_p]:text-muted-foreground
              [&_img]:rounded-lg [&_img]:max-w-full [&_img]:my-3
              [&_strong]:text-foreground [&_strong]:font-semibold
              [&_em]:italic
              [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2
              [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2
              [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-3 [&_blockquote]:italic"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        ) : (
          <p>{previewText}</p>
        )}
      </div>
      
      {needsTruncation && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-primary hover:text-primary/80 p-0 h-auto font-medium text-xs"
        >
          {isExpanded ? (
            <><ChevronUp className="w-3 h-3 mr-1" /> Réduire</>
          ) : (
            <><ChevronDown className="w-3 h-3 mr-1" /> Lire plus</>
          )}
        </Button>
      )}
    </div>
  );
};

export const OrderConfirmation = ({ items: propItems, onReturnHome, orderId }) => {
  const [randomArticle, setRandomArticle] = useState(null);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);

  // Use items from props if available, otherwise fetch from order
  const items = (propItems && propItems.length > 0) ? propItems : (orderData?.items || []);

  useEffect(() => {
    fetchArticles();
    // If no items in props, fetch order data
    if ((!propItems || propItems.length === 0) && orderId) {
      fetchOrderData();
    } else {
      setLoadingOrder(false);
    }
  }, [orderId, propItems]);

  const fetchOrderData = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrderData(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement de la commande:", error);
    } finally {
      setLoadingOrder(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/culture?limit=100`);
      const allArticles = response.data || [];
      // Sélectionner un article pour la page de confirmation
      // Utilise un hash basé sur orderId + "_page" pour être différent de l'email
      if (allArticles.length > 0 && orderId) {
        // Simple hash function pour sélectionner un article différent de l'email
        let hash = 0;
        const seedString = orderId + "_page";
        for (let i = 0; i < seedString.length; i++) {
          hash = ((hash << 5) - hash) + seedString.charCodeAt(i);
          hash = hash & hash; // Convert to 32bit integer
        }
        const pageIndex = Math.abs(hash) % allArticles.length;
        setRandomArticle(allArticles[pageIndex]);
      } else if (allArticles.length > 0) {
        // Fallback: random selection if no orderId
        const randomIndex = Math.floor(Math.random() * allArticles.length);
        setRandomArticle(allArticles[randomIndex]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des articles:", error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const getTotalPrice = () => {
    // Priority: orderData.total > calculate from items > 0
    if (orderData?.total !== undefined && orderData?.total !== null) {
      return orderData.total;
    }
    if (items && items.length > 0) {
      return items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
    }
    return 0;
  };

  // Show loading while fetching order
  if (loadingOrder) {
    return (
      <div className="min-h-screen bg-background/40 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background/40 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
            <CheckCircle className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl text-street tracking-wide mb-2">
            Commande confirmée !
          </h1>
          <p className="text-muted-foreground">
            Vos billets sont prêts à être téléchargés
          </p>
        </div>

        {/* Point Culture - UN SEUL article aléatoire */}
        {randomArticle && (
          <div className="glass-card rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold">Point Culture</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Découvrez l'histoire de la musique urbaine
            </p>
            <CultureArticleCard article={randomArticle} />
          </div>
        )}

        {/* QR Code Tickets */}
        {orderId && items.length > 0 && (
          <div className="glass-card rounded-lg p-6 sm:p-8 mb-6 border-2 border-primary/30">
            <div className="flex items-center gap-2 mb-6">
              <Ticket className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">Vos billets</h2>
              <span className="ml-auto text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded">
                Téléchargez maintenant !
              </span>
            </div>
            <div className="space-y-6">
              {items.map((item, index) => (
                <TicketQRCode 
                  key={index}
                  order={{ id: orderId }}
                  item={item}
                  index={index}
                />
              ))}
            </div>
          </div>
        )}

        {/* No items message */}
        {items.length === 0 && (
          <div className="glass-card rounded-lg p-6 mb-6 text-center">
            <p className="text-muted-foreground">
              Les détails de votre billet seront envoyés par email.
            </p>
            {orderId && (
              <p className="text-sm text-muted-foreground mt-2">
                Commande: <span className="font-mono text-primary">{orderId.slice(0, 8)}</span>
              </p>
            )}
          </div>
        )}

        {/* Email notification */}
        <div className="glass-card rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold mb-1 text-sm">Vos billets arrivent aussi par email !</h3>
              <p className="text-xs text-muted-foreground">
                Un email de confirmation avec vos e-billets sera envoyé dans quelques instants.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-2 italic">
                Vous ne trouvez pas l'email ? Pensez à vérifier vos spams ou courriers indésirables. L'email est envoyé depuis noreply@tikaculture.com
              </p>
            </div>
          </div>
        </div>

        {/* Order Details - Compact */}
        {(items.length > 0 || orderData?.total) && (
          <div className="glass-card rounded-lg p-6 mb-6">
            <h2 className="text-lg font-bold mb-4">Récapitulatif</h2>
            {items.length > 0 && (
              <div className="space-y-2 mb-4">
                {items.map((item, index) => (
                  <div
                    key={index}
                    data-testid={`order-item-${index}`}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="flex-1">
                      <span className="font-medium">{item.event_title}</span>
                      <span className="text-muted-foreground ml-2">
                        ({item.ticket_type} × {item.quantity})
                      </span>
                    </div>
                    <span className="font-bold text-primary">
                      {((item.price || 0) * (item.quantity || 1)).toFixed(2)}€
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center pt-4 border-t border-border">
              <span className="font-semibold">Total payé</span>
              <span className="text-xl font-bold text-primary">
                {getTotalPrice().toFixed(2)}€
              </span>
            </div>
          </div>
        )}

        {/* Cultural Fact - "Le savais-tu ?" */}
        {items.length > 0 && (
          <div className="mb-6">
            <DidYouKnow 
              category={items[0]?.event_category} 
              orderId={orderId}
            />
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button
            data-testid="return-home-btn"
            onClick={onReturnHome}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg"
          >
            <Home className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Besoin d'aide ? Contactez-nous à support@tikaculture.com</p>
        </div>
      </div>
    </div>
  );
};
